import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useActingOrg } from '@/auth/actingOrgContext';
import { createAppRole, normalizeApiErrorDetails, type AppRoleOption } from '@/api/console';
import { fetchOrganizations, OrgAdminApiError } from '@/api/orgAdmin';
import { useToast } from '@/components/ui';

/**
 * EAA-1841: the app's own identity, narrowed to what this form needs. Taking a
 * two-field shape rather than `AdminRegistryApp` is what lets the coverage
 * board (whose rows are `AppCoverageRow`, not registry rows) reuse the same
 * form without either side widening its types.
 */
export interface AppRoleTargetApp {
    code: string;
    name: string;
}

const EMPTY_FORM = { key: '', name: '', description: '' };

/**
 * EAA-1841: stricter than portal-api's `SlugField` (which also accepts upper
 * case), so a key accepted here is always accepted there. Underscores are
 * allowed on purpose — ACH permission-group slugs use them (`teacher_lead`),
 * unlike the app `code` pattern in `useCreateAppForm`.
 */
const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

/** How many orgs one page of the picker holds before the operator must search. */
const ORG_PAGE_SIZE = 50;

/**
 * EAA-1841: form state, organization picker and submit for `CreateAppRoleModal`.
 *
 * The organization field is not incidental — `POST /admin/app-roles` REQUIRES
 * it, because the role lands in an app-local scope (for ACH, a tenant) that
 * portal-api resolves from the org through `OrganizationAppScope`. None of the
 * three surfaces that host this form can supply one implicitly: `/console/apps`
 * is super-admin-wide, and the role-coverage board is deliberately
 * default-scope only (see `useRoleCoverageData`'s docstring). So the form asks,
 * pre-selecting the acting org when there is one.
 *
 * `permissions` is deliberately never sent. The receiver refuses unknown
 * permission codes outright and no endpoint exposes an app's valid codes, so a
 * free-text field here would mostly produce 400s; a group with no permissions
 * grants nothing, and the operator sets them inside the app.
 */
export function useCreateAppRoleForm(
    app: AppRoleTargetApp | null,
    onCreated?: (role: AppRoleOption, organizationId: string) => void
) {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const actingOrgId = useActingOrg()?.actingOrgId ?? null;

    const [form, setForm] = useState(EMPTY_FORM);
    const [organizationId, setOrganizationId] = useState('');
    const [orgSearch, setOrgSearch] = useState('');
    const [debouncedOrgSearch, setDebouncedOrgSearch] = useState('');
    const [keyTouched, setKeyTouched] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [formError, setFormError] = useState<string | null>(null);

    const open = app !== null;

    // Reset every time the modal targets a different app: a key/name typed for
    // one app means nothing in another app's catalog.
    useEffect(() => {
        if (!open) return;
        setForm(EMPTY_FORM);
        setOrganizationId(actingOrgId ?? '');
        setOrgSearch('');
        setDebouncedOrgSearch('');
        setKeyTouched(false);
        setFieldErrors({});
        setFormError(null);
        // Only re-run when the target app changes; `actingOrgId` is read as the
        // initial value and must not re-clear a picked org if it settles later.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app?.code, open]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedOrgSearch(orgSearch.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [orgSearch]);

    const orgsQuery = useQuery({
        queryKey: ['app-role-orgs', debouncedOrgSearch, accessToken],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                pageSize: ORG_PAGE_SIZE,
                ordering: 'name',
                ...(debouncedOrgSearch ? { search: debouncedOrgSearch } : {}),
            }),
        enabled: open && Boolean(accessToken),
        retry: false,
    });

    // EAA-1841: an inactive org still owns its scopes, but creating a role for
    // one is almost certainly a mistake — keep them out of the picker rather
    // than letting the server accept the write.
    const orgs = useMemo(
        () => (orgsQuery.data?.results ?? []).filter((org) => org.is_active),
        [orgsQuery.data]
    );
    const orgOverflow = (orgsQuery.data?.count ?? 0) > orgs.length;

    function setField<K extends keyof typeof EMPTY_FORM>(key: K, value: string) {
        setForm((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: [] } : prev));
        setFormError(null);
    }

    const trimmedKey = form.key.trim();
    const trimmedName = form.name.trim();
    const keyValid = KEY_PATTERN.test(trimmedKey);
    const keyError =
        keyTouched && trimmedKey && !keyValid
            ? 'Mã vai trò chỉ gồm chữ thường, số, gạch ngang và gạch dưới, bắt đầu bằng chữ hoặc số.'
            : null;

    const mutation = useMutation({
        mutationFn: () =>
            createAppRole(config.apiBase, accessToken as string, {
                application: (app as AppRoleTargetApp).code,
                organization_id: organizationId,
                key: trimmedKey,
                name: trimmedName,
                ...(form.description.trim() ? { description: form.description.trim() } : {}),
            }),
        onSuccess: (role) => {
            // The new row is ORG-SCOPED (`AppRole.organization=org` server-side),
            // so every picker that reads the catalog for this org must refetch —
            // `admin-app-roles` is keyed by app code, hence the prefix-wide
            // invalidation rather than one exact key.
            void queryClient.invalidateQueries({ queryKey: ['admin-app-roles'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-coverage'] });
            toast.success(`Đã tạo vai trò "${role.key}" trong ${app?.name ?? 'ứng dụng'}.`);
            onCreated?.(role, organizationId);
        },
        onError: (error: unknown) => {
            if (error instanceof OrgAdminApiError) {
                // EAA-1884: `.details` entries are `{code, message}` objects on
                // the wire — see `normalizeApiErrorDetails`'s own doc comment.
                const details = normalizeApiErrorDetails(error.details);
                // `application` carries the receiver's OWN refusal verbatim
                // (unknown permission code, system group, slug owned by another
                // tenant) and has no field to attach to here — the app is fixed
                // by whoever opened this modal — so it reads as a form error.
                const appDetail = details.application?.[0];
                if (appDetail) {
                    setFormError(appDetail);
                    return;
                }
                if (Object.keys(details).length > 0) {
                    setFieldErrors(details);
                    return;
                }
                setFormError(error.message);
                return;
            }
            setFormError('Không thể tạo vai trò trong ứng dụng.');
        },
    });

    const canSubmit = Boolean(app) && keyValid && trimmedName.length > 0 && Boolean(organizationId);

    function onSubmit(event: FormEvent) {
        event.preventDefault();
        if (!canSubmit || mutation.isPending) return;
        setFormError(null);
        mutation.mutate();
    }

    const selectedOrg = orgs.find((org) => org.id === organizationId) ?? null;

    return {
        app,
        open,
        form,
        setField,
        organizationId,
        setOrganizationId,
        selectedOrg,
        orgSearch,
        setOrgSearch,
        orgs,
        orgsLoading: orgsQuery.isLoading,
        orgsError: orgsQuery.isError,
        orgOverflow,
        keyError,
        onKeyBlur: () => setKeyTouched(true),
        fieldErrors,
        formError,
        canSubmit,
        pending: mutation.isPending,
        onSubmit,
    };
}

export type CreateAppRoleFormVM = ReturnType<typeof useCreateAppRoleForm>;
